import React, { useEffect, useRef } from 'react';
import { styled } from 'styled-components';

import { DesktopShow, DeviceQueries } from 'Common/components/styled';
import BlueSpinner from 'Common/components/BlueSpinner';
import useFirebaseRealtimeDatabaseChildren from 'Common/hooks/useRealtimeDatabaseChildren';
import { NotificationOutlet } from 'Common/contexts/NotificationContext';

import CompassHeader, { PageType } from 'Compass/components/CompassHeader';
import { OrderTypeFilterNames, useOrdersContext } from 'Compass/contexts/OrdersContext';
import OrderFilters from 'Compass/components/orders/OrderFilters';
import OrderRow from 'Compass/components/orders/OrderRow';
import { Page } from 'Compass/core/MainLayout';
import { PageCard, PageTitle } from 'Compass/components/styled/PageLayout';
import { app } from 'Compass/api/Firebase';
import OrderPagination from 'Compass/components/orders/OrderPagination';

const OrdersPage = () => {
  const lastModifiedTime = useFirebaseRealtimeDatabaseChildren<string>(`dazzle/order`, app);
  const ordersContext = useOrdersContext();

  const firstFirebaseLoad = useRef(true);
  const lastModifiedTimeFetched = useRef<string | undefined>(undefined);

  useEffect(() => {
    void ordersContext.fetchOrders();
  }, [ordersContext]);

  useEffect(() => {
    if (!lastModifiedTime) {
      // do nothing
    } else if (firstFirebaseLoad.current) {
      firstFirebaseLoad.current = false;
    } else if (+(lastModifiedTimeFetched.current ?? Date.now()) < +lastModifiedTime) {
      lastModifiedTimeFetched.current = lastModifiedTime;
      void ordersContext.fetchOrders(true);
    }
  }, [lastModifiedTime, ordersContext]);

  const orders = ordersContext.orders;

  return <>
    <CompassHeader pageType={PageType.Orders}/>
    <Page>
      <NotificationOutlet/>
      <PageCard>
        <PageTitle role="page-title">Orders</PageTitle>
        <OrderTableContainer>
          <OrderFilters/>
          <OrderTable>
            <DesktopShow>
              <TableHeader>{OrderTypeFilterNames[ordersContext.orderTypeFilter]}</TableHeader>
              <TableHeader>Price</TableHeader>
              <TableHeader>User Id</TableHeader>
              <TableHeader>Status</TableHeader>
              <TableHeader>Assigned To</TableHeader>
              <TableHeader>Last Updated</TableHeader>
            </DesktopShow>

            {
              orders ? orders.map(order => <OrderRow order={order} key={order.order_id}/>) :
                <SpinnerContainer><BlueSpinner/></SpinnerContainer>
            }
            {
              orders && orders?.length === 0 && <SpinnerContainer>There are no orders.</SpinnerContainer>
            }
          </OrderTable>
          <OrderPagination/>
        </OrderTableContainer>
      </PageCard>
    </Page>
  </>;
};


const OrderTableContainer = styled.div`
  margin-top: 24px;

  @media (${DeviceQueries.mobile}) {
    margin-top: 0;
  }
`;

const OrderTable = styled.div`
  display: grid;
  grid-template-columns: 2fr 1fr 1.5fr 1fr 1fr 1.5fr;
  gap: 0;
  border: 1px solid #E4E7EF;
  border-radius: 12px;
  overflow: hidden;

  @media (${DeviceQueries.mobile}) {
    display: flex;
    flex-direction: column;
    gap: 16px;
    padding: 16px;
    border: none;
  }
`;

const TableHeader = styled.div`
  background: #F8F9FC;
  padding: 10px 16px;
  font-weight: 600;
  font-size: 11px;
  color: #5A6178;
  text-transform: uppercase;
  letter-spacing: 0.6px;
  border-bottom: 1px solid #E4E7EF;
`;

const SpinnerContainer = styled.div`
  width: 100%;
  padding: 25px;
  grid-column: 1/7;
  text-align: center;
`;

export default OrdersPage;
